import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const WishlistFilters = ({ filters, onFiltersChange, onCreateNew }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const privacyOptions = [
    { value: 'all', label: 'All Privacy', icon: 'Globe' },
    { value: 'private', label: 'Private', icon: 'Lock' },
    { value: 'family', label: 'Family', icon: 'Home' },
    { value: 'friends', label: 'Friends', icon: 'Users' },
    { value: 'public', label: 'Public', icon: 'Globe' }
  ];

  const sortOptions = [
    { value: 'recent', label: 'Recently Updated' },
    { value: 'name', label: 'Name A-Z' },
    { value: 'items', label: 'Most Items' },
    { value: 'views', label: 'Most Viewed' },
    { value: 'completion', label: 'Completion Rate' }
  ];

  const occasionOptions = [
    { value: 'all', label: 'All Occasions' },
    { value: 'birthday', label: 'Birthday' },
    { value: 'christmas', label: 'Christmas' },
    { value: 'anniversary', label: 'Anniversary' },
    { value: 'wedding', label: 'Wedding' },
    { value: 'graduation', label: 'Graduation' },
    { value: 'baby-shower', label: 'Baby Shower' },
    { value: 'housewarming', label: 'Housewarming' },
    { value: 'other', label: 'Other' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  return (
    <div className="bg-white rounded-2xl shadow-md p-6 mb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <h2 className="font-heading font-semibold text-xl text-accent-800">
            My Wishlists
          </h2>
          <div className="flex items-center space-x-2 px-3 py-1 bg-primary-50 rounded-full">
            <Icon name="List" size={16} className="text-primary" />
            <span className="text-sm font-medium text-primary">
              {filters.totalCount} lists
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-accent-600"
          >
            Filters
          </Button>
          <Button
            variant="primary"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={onCreateNew}
            className="shadow-warm"
          >
            Create Wishlist
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Icon
          name="Search"
          size={20}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 text-accent-400"
        />
        <Input
          type="search"
          placeholder="Search your wishlists..."
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          className="pl-12 pr-4 py-3 w-full bg-surface border-0 rounded-xl focus:ring-2 focus:ring-primary/20"
        />
      </div>

      {/* Expanded Filters */}
      {isExpanded && (
        <div className="space-y-6 pt-4 border-t border-accent-100">
          {/* Quick Filters */}
          <div>
            <h3 className="font-medium text-accent-800 mb-3">Quick Filters</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'recent', label: 'Recently Updated', icon: 'Clock' },
                { key: 'shared', label: 'Shared with Me', icon: 'Users' },
                { key: 'collaborative', label: 'Collaborative', icon: 'UserPlus' },
                { key: 'upcoming', label: 'Upcoming Occasions', icon: 'Calendar' },
                { key: 'high-priority', label: 'High Priority Items', icon: 'AlertCircle' }
              ].map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => handleFilterChange('quickFilter', filter.key)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${filters.quickFilter === filter.key
                      ? 'bg-primary text-white' : 'bg-surface text-accent-600 hover:bg-accent-100'
                    }`}
                >
                  <Icon name={filter.icon} size={14} />
                  <span>{filter.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Filter Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Privacy Filter */}
            <div>
              <label className="block text-sm font-medium text-accent-700 mb-2">
                Privacy Level
              </label>
              <select
                value={filters.privacy}
                onChange={(e) => handleFilterChange('privacy', e.target.value)}
                className="w-full px-4 py-2 bg-surface border-0 rounded-lg focus:ring-2 focus:ring-primary/20 text-accent-700"
              >
                {privacyOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Occasion Filter */}
            <div>
              <label className="block text-sm font-medium text-accent-700 mb-2">
                Occasion
              </label>
              <select
                value={filters.occasion}
                onChange={(e) => handleFilterChange('occasion', e.target.value)}
                className="w-full px-4 py-2 bg-surface border-0 rounded-lg focus:ring-2 focus:ring-primary/20 text-accent-700"
              >
                {occasionOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort Filter */}
            <div>
              <label className="block text-sm font-medium text-accent-700 mb-2">
                Sort By
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="w-full px-4 py-2 bg-surface border-0 rounded-lg focus:ring-2 focus:ring-primary/20 text-accent-700"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Active Filters */}
          {(filters.search || filters.privacy !== 'all' || filters.occasion !== 'all' || filters.quickFilter) && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-accent-600">Active filters:</span>
              <div className="flex flex-wrap gap-2">
                {filters.search && (
                  <div className="flex items-center space-x-1 px-2 py-1 bg-primary-100 text-primary-700 rounded-lg text-xs">
                    <span>Search: "{filters.search}"</span>
                    <button onClick={() => handleFilterChange('search', '')}>
                      <Icon name="X" size={12} />
                    </button>
                  </div>
                )}
                {filters.privacy !== 'all' && (
                  <div className="flex items-center space-x-1 px-2 py-1 bg-primary-100 text-primary-700 rounded-lg text-xs">
                    <span>Privacy: {filters.privacy}</span>
                    <button onClick={() => handleFilterChange('privacy', 'all')}>
                      <Icon name="X" size={12} />
                    </button>
                  </div>
                )}
                {filters.occasion !== 'all' && (
                  <div className="flex items-center space-x-1 px-2 py-1 bg-primary-100 text-primary-700 rounded-lg text-xs">
                    <span>Occasion: {filters.occasion}</span>
                    <button onClick={() => handleFilterChange('occasion', 'all')}>
                      <Icon name="X" size={12} />
                    </button>
                  </div>
                )}
                {filters.quickFilter && (
                  <div className="flex items-center space-x-1 px-2 py-1 bg-primary-100 text-primary-700 rounded-lg text-xs">
                    <span>Filter: {filters.quickFilter}</span>
                    <button onClick={() => handleFilterChange('quickFilter', '')}>
                      <Icon name="X" size={12} />
                    </button>
                  </div>
                )}
                <button
                  onClick={() => onFiltersChange({
                    search: '',
                    privacy: 'all',
                    occasion: 'all',
                    sortBy: 'recent',
                    quickFilter: '',
                    totalCount: filters.totalCount
                  })}
                  className="text-xs text-accent-600 hover:text-accent-800 underline"
                >
                  Clear all
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WishlistFilters;